import React, { useState, useEffect } from 'react';
import { Modal, Box, TextField, Button, Typography, IconButton, Grid } from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrency } from '../../context/CurrencyContext';

interface PriceConversionModalProps {
  open: boolean;
  onClose: () => void;
  initialPrice?: number; 
  currency?: string;
  sx?: object;
}

const PriceConversionModal: React.FC<PriceConversionModalProps> = ({ open, onClose, initialPrice, currency }) => {
  const [price, setPrice] = useState('');
  const [convertedPrice, setConvertedPrice] = useState<number | null>(null);
  const [isColones, setIsColones] = useState<boolean>(true);  // Inicializamos con true, lo validaremos más tarde
  const { sellRate, buyRate } = useCurrency();
  

  useEffect(() => {
    if (open && initialPrice !== undefined) {
      setPrice(initialPrice.toString());
    }
  }, [open, initialPrice]);

  useEffect(() => {
    if (currency) {
      // Validamos si la moneda es "CRC" (Colones)
      setIsColones(currency === 'CRC');
    }
  }, [currency]);  // Actualizamos el estado `isColones` cuando cambie `currency`

  const formatPrice = (price: number, currency: string) => {
    let locale = 'es-CR';
    let symbol = '';

    switch (currency) {
      case 'Colones':
        locale = 'es-CR';
        symbol = '₡';
        break;
      case 'Dólares':
        locale = 'en-US';
        symbol = '$';
        break;
      default:
        locale = 'en-US';
        symbol = '$';
    }

    return `${symbol}${price.toLocaleString(locale)} ${currency}`;
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(event.target.value);
  };

  const handleClearPrice = () => {
    setPrice('');
    setConvertedPrice(null);
  };

  const handleConvert = () => {
    if (price) {
      let convertedPriceValue;
      
      const validBuyRate = buyRate ?? 1;
      const validSellRate = sellRate ?? 1;

      if (isColones) {
        convertedPriceValue = parseFloat(price) / validSellRate;  // Convertir a Dólares
      } else {
        convertedPriceValue = parseFloat(price) * validBuyRate;  // Convertir a Colones
      }
      setConvertedPrice(convertedPriceValue);
    } else {
      setConvertedPrice(null); 
    }
  };

  const handleCurrencySwitch = () => {
    setIsColones(!isColones);  // Cambiar entre Colones y Dólares
  };

  return (
    <>
      {/* Modal de conversión de precios */}
      <Modal open={open} onClose={onClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '350px',
          bgcolor: 'white',
          padding: 3,
          borderRadius: 1,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <IconButton onClick={onClose} sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'gray',
          }}>
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ textAlign: 'center', marginBottom: 2 }}>
            Conversión de Precio
          </Typography>

          <Grid container spacing={2} sx={{ marginBottom: 2, justifyContent: 'center', alignItems: 'center' }}>
            <Grid item>
              {/* Ícono de cambio de moneda */}
              <IconButton onClick={handleCurrencySwitch} sx={{ color: 'green' }}>
                <CurrencyExchangeIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                Tipo de cambio de {isColones ? 'Venta' : 'Compra'}
              </Typography>
            </Grid>
          </Grid>

          <TextField
            label={isColones ? "Precio en Colones" : "Precio en Dólares"}
            type="number"
            value={price}
            onChange={handlePriceChange}
            fullWidth
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'green', 
                },
              },
              '& .MuiInputLabel-root': {
                '&.Mui-focused': {
                  color: 'green',  
                },
              },
            }}
          />

          <Button
            variant="contained"
            color="success"
            onClick={handleConvert}
            fullWidth
            sx={{ marginBottom: 2 }}
          >
            Convertir
          </Button>

          {convertedPrice !== null && (
            <Typography variant="body1" sx={{ textAlign: 'center', marginBottom: 2 }}>
              {isColones
                ? formatPrice(convertedPrice, 'Dólares')
                : formatPrice(convertedPrice, 'Colones')}
            </Typography>
          )}

          {/* Botón para limpiar el precio */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearPrice}
            fullWidth
            sx={{ marginTop: 1}}  
          >
            Limpiar
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default PriceConversionModal;
